// Login-Gate (UX §3.1), Keine-Rechte-Bildschirm (K6 als Shell-Zustand) und Fehlerzustand der Shells.
// Öffentliche Routen (Login, Galerie …) liegen außerhalb der Shell und laufen nie durch das Gate.
import { AuthLayout, Monogram, Spinner } from '@bar/ui/basis';
import { LockSimple, WarningCircle } from '@phosphor-icons/react';
import { safeNextPath } from '@web/lib';
import type { PlatformKey } from '@web/registry/types';
import { type ReactNode, useEffect } from 'react';
import { useShellIdentity } from './data';
import type { ShellIdentity } from './identity';
import { isNavigatingAway, navigateAway } from './navigation';
import { PlatformProviders } from './providers';

/** Wohin ein nicht angemeldeter Aufruf führt (Bar AI hat keinen eigenen Login, UX A12). */
export function loginUrl(
  platform: PlatformKey,
  location: Pick<Location, 'pathname' | 'search'>,
): string {
  const next = safeNextPath(`${location.pathname}${location.search}`);
  const base = platform === 'admin' ? '/admin/login' : '/login';
  return next && next !== '/' ? `${base}?next=${encodeURIComponent(next)}` : base;
}

const PLATFORM_NAME: Record<PlatformKey, string> = {
  people: 'Bar',
  admin: 'Bar Admin',
  ai: 'Bar AI',
};

const pillLink =
  'inline-flex h-12 items-center justify-center rounded-full bg-primary px-6 text-button text-on-primary transition-colors duration-(--bar-dur-fast) hover:bg-primary-hover';

function ShellLoading() {
  return (
    <div
      aria-busy="true"
      className="flex min-h-dvh items-center justify-center bg-page text-fg-secondary"
    >
      <span className="inline-flex items-center gap-3 text-label">
        <Monogram size={28} />
        <Spinner />
        <span className="sr-only">Loading</span>
      </span>
    </div>
  );
}

function StateCard({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div data-ready="true" className="flex flex-col gap-4 rounded-xl bg-card p-6 light:shadow-1">
      <span className="flex size-14 items-center justify-center rounded-full bg-accent-soft text-accent-fg">
        {icon}
      </span>
      {children}
    </div>
  );
}

/** Kern antwortet mit einem Fehler (nicht 401): Hinweis mit Retry statt Umleitung. */
export function ShellError({ platform }: { platform: PlatformKey }) {
  return (
    <AuthLayout platform={platform} title="We couldn’t load Bar">
      <StateCard icon={<WarningCircle size={28} aria-hidden />}>
        <p className="text-body text-fg-secondary">
          The server didn’t answer as expected. Check your connection and try again.
        </p>
        <button type="button" className={pillLink} onClick={() => window.location.reload()}>
          Retry
        </button>
      </StateCard>
    </AuthLayout>
  );
}

/** Plattform ohne Recht (v. a. Bar Admin ohne Admin-fähige Rolle): Hinweis mit Link zur People-App. */
export function NoPlatformAccess({ platform, name }: { platform: PlatformKey; name: string }) {
  return (
    <AuthLayout
      platform={platform}
      title={`You don’t have access to ${PLATFORM_NAME[platform]}`}
      subtitle={`Signed in as ${name}.`}
    >
      <StateCard icon={<LockSimple size={28} aria-hidden />}>
        <p className="text-body text-fg-secondary">
          {platform === 'admin'
            ? 'Bar Admin is for Admin, HR, Performance, Compensation and C-level roles. Ask an admin if you need access.'
            : 'Your account can’t open this area. Ask an admin if you need access.'}
        </p>
        {platform === 'people' ? null : (
          <a href="/" className={pillLink}>
            Go to Bar
          </a>
        )}
      </StateCard>
    </AuthLayout>
  );
}

const isLoginPath = (pathname: string) => /^\/(admin\/)?login(\/|$)/.test(pathname);

function ShellGate({
  platform,
  children,
}: {
  platform: PlatformKey;
  children: (identity: ShellIdentity) => ReactNode;
}) {
  const state = useShellIdentity();
  const anonymous = state.status === 'anonymous';
  useEffect(() => {
    if (!anonymous || isLoginPath(window.location.pathname) || isNavigatingAway()) return;
    // Volle Navigation: /login gehört der People-App (bzw. /admin/login der Admin-Plattform).
    navigateAway(loginUrl(platform, window.location), { replace: true });
  }, [anonymous, platform]);
  if (state.status === 'error') return <ShellError platform={platform} />;
  if (state.status !== 'ready') return <ShellLoading />;
  if (!state.identity.platforms[platform])
    return <NoPlatformAccess platform={platform} name={state.identity.name} />;
  return <>{children(state.identity)}</>;
}

/**
 * Rahmen jeder Shell: Provider (Theme mit Konto-Spiegel, Router-Link) und Gate. Den AuthProvider
 * legt der Plattform-Start um den Router (auch Login-Seiten brauchen ihn). Die Shell selbst bekommt
 * nur eine angemeldete Identität mit Recht auf die Plattform.
 */
export function PlatformShell({
  platform,
  children,
}: {
  platform: PlatformKey;
  children: (identity: ShellIdentity) => ReactNode;
}) {
  return (
    <PlatformProviders syncAccount>
      <ShellGate platform={platform}>{children}</ShellGate>
    </PlatformProviders>
  );
}
